// Client Portal - Fleet API Service
// Handles fleet-related API calls for client portal users

import axios from 'axios';
import { store } from '../../../store';
import { getToken, getRefreshToken, setToken } from '../../../shared/utils/tokenStorage';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = getRefreshToken();
        const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
          refresh: refreshToken,
        });
        setToken(response.data.access);
        originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Fleet API Service for Client Portal
class FleetAPIService {
  // ============= Fleet Overview =============

  // Get fleet overview dashboard data
  async getFleetOverview() {
    try {
      const response = await apiClient.get('/client/fleet/overview/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching fleet overview:', error);
      return {
        success: false,
        error: error.message,
        data: {
          total_vehicles: 0,
          active_vehicles: 0,
          total_drivers: 0,
          active_drivers: 0,
          total_trips_today: 0,
          total_distance_today: 0,
          fuel_consumed_today: 0,
          active_alerts: 0,
        },
      };
    }
  }

  // ============= Vehicles =============

  // Get client's vehicles
  async getMyVehicles(params = {}) {
    try {
      const response = await apiClient.get('/fleet/vehicles/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get vehicle details
  async getVehicleDetails(vehicleId) {
    try {
      const response = await apiClient.get(`/fleet/vehicles/${vehicleId}/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching vehicle details:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Get vehicle current location
  async getVehicleLocation(vehicleId) {
    try {
      const response = await apiClient.get(`/fleet/vehicles/${vehicleId}/location/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching vehicle location:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Get vehicle trip history
  async getVehicleTrips(vehicleId, params = {}) {
    try {
      const response = await apiClient.get(`/fleet/vehicles/${vehicleId}/trips/`, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching vehicle trips:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get vehicle route playback
  async getVehicleRoute(vehicleId, date) {
    try {
      const response = await apiClient.get(`/fleet/vehicles/${vehicleId}/route/`, {
        params: { date },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching vehicle route:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // ============= Drivers =============

  // Get client's drivers
  async getMyDrivers(params = {}) {
    try {
      const response = await apiClient.get('/fleet/drivers/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching drivers:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get driver details
  async getDriverDetails(driverId) {
    try {
      const response = await apiClient.get(`/fleet/drivers/${driverId}/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching driver details:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Get driver performance
  async getDriverPerformance(driverId, params = {}) {
    try {
      const response = await apiClient.get(`/fleet/drivers/${driverId}/performance/`, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching driver performance:', error);
      return {
        success: false,
        error: error.message,
        data: {
          driving_score: 0,
          total_distance: 0,
          total_trips: 0,
          average_speed: 0,
          harsh_events: 0,
          fuel_efficiency: 0,
        },
      };
    }
  }

  // Get driver violations
  async getDriverViolations(driverId, params = {}) {
    try {
      const response = await apiClient.get(`/fleet/drivers/${driverId}/violations/`, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching driver violations:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // ============= Live Tracking =============

  // Get all vehicles live positions
  async getLiveFleetPositions() {
    try {
      const response = await apiClient.get('/client/fleet/live-positions/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching live positions:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Start tracking a vehicle
  async startVehicleTracking(vehicleId) {
    try {
      const response = await apiClient.post(`/fleet/vehicles/${vehicleId}/start-tracking/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error starting vehicle tracking:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Stop tracking a vehicle
  async stopVehicleTracking(vehicleId) {
    try {
      const response = await apiClient.post(`/fleet/vehicles/${vehicleId}/stop-tracking/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error stopping vehicle tracking:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ============= Analytics =============

  // Get fleet analytics
  async getFleetAnalytics(params = {}) {
    try {
      const response = await apiClient.get('/client/fleet/analytics/', {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching fleet analytics:', error);
      return {
        success: false,
        error: error.message,
        data: {},
      };
    }
  }

  // Get fuel consumption report
  async getFuelConsumptionReport(params = {}) {
    try {
      const response = await apiClient.get('/client/fleet/fuel-consumption/', {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching fuel consumption:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get utilization report
  async getUtilizationReport(params = {}) {
    try {
      const response = await apiClient.get('/client/fleet/utilization/', {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching utilization report:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get cost analysis
  async getCostAnalysis(params = {}) {
    try {
      const response = await apiClient.get('/client/fleet/cost-analysis/', {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching cost analysis:', error);
      return {
        success: false,
        error: error.message,
        data: {},
      };
    }
  }

  // ============= Geofencing =============

  // Get client's geofences
  async getMyGeofences() {
    try {
      const response = await apiClient.get('/client/geofences/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching geofences:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get geofence violations
  async getGeofenceViolations(params = {}) {
    try {
      const response = await apiClient.get('/client/geofence-violations/', {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching geofence violations:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // ============= Maintenance =============

  // Get maintenance schedule
  async getMaintenanceSchedule(params = {}) {
    try {
      const response = await apiClient.get('/client/maintenance/schedule/', {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching maintenance schedule:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get maintenance history
  async getMaintenanceHistory(params = {}) {
    try {
      const response = await apiClient.get('/client/maintenance/history/', {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching maintenance history:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Request maintenance
  async requestMaintenance(vehicleId, maintenanceData) {
    try {
      const response = await apiClient.post('/client/maintenance/request/', {
        vehicle_id: vehicleId,
        ...maintenanceData,
        requested_by: store.getState().auth?.user?.id,
        requested_at: new Date().toISOString(),
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error requesting maintenance:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ============= Reports =============

  // Generate fleet report
  async generateFleetReport(reportType, params = {}) {
    try {
      const response = await apiClient.post('/client/reports/generate/', {
        report_type: reportType,
        ...params,
        requested_by: store.getState().auth?.user?.id,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error generating report:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Download report
  async downloadReport(reportId, format = 'pdf') {
    try {
      const response = await apiClient.get(`/client/reports/${reportId}/download/`, {
        params: { format },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return {
        success: true,
      };
    } catch (error) {
      console.error('Error downloading report:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Get scheduled reports
  async getScheduledReports() {
    try {
      const response = await apiClient.get('/client/reports/scheduled/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching scheduled reports:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }
}

// Export singleton instance
const fleetAPI = new FleetAPIService();

export default fleetAPI;
export { FleetAPIService };
